// @ts-check
'use_strict';

declare var $;
declare var paypal;

/**
 * Class for managing client-side PayPal functionality.
 */
export class PayPal {
    /**
     * @var {Function} totals_getter Function for getting the totals to be used when creating an order.
     * @var {Function} approve_callback Function to be called when the user approves an order.
     * @var {HTMLInputElement} order_id_input Input for the PayPal order ID.
     * @var {HTMLInputElement} payer_id_input Input for the PayPal payer ID.
     * @var {boolean} enabled Flag for whether the PayPal integration script exists.
     */
    totals_getter: Function;
    approve_callback: Function;
    order_id_input: HTMLInputElement;
    payer_id_input: HTMLInputElement;
    enabled: boolean = true;

    /**
     * Instantiate the class and render the buttons.
     * 
     * @param totals_getter Function for getting the totals to be used when creating an order.
     * @param approve_callback Function to be called when the user approves an order.
     * @param render_on_init Flag for whether the buttons should be rendered as soon as the class is instantiated.
     * @returns {PayPal}
     */
    constructor(totals_getter: Function, approve_callback: Function, render_on_init: boolean = true) {
        // Flag as disabled if PayPal integration script is disabled or broken.
        if (typeof paypal === 'undefined') {
            this.enabled = false;
            return;
        }

        this.totals_getter = totals_getter;
        this.approve_callback = approve_callback;
        this.order_id_input = <HTMLInputElement>document.getElementById('js-paypal-order-id');
        this.payer_id_input = <HTMLInputElement>document.getElementById('js-paypal-payer-id');

        if (render_on_init) {
            this.render_buttons();
        }
    }

    /**
     * Render buttons in paypal button containers if they haven't been rendered yet.
     * 
     * @returns {void}
     */
    render_buttons(): void {
        // Get all button containers with id that begins with 'paypal-button-container'.
        const button_containers = document.querySelectorAll('[id^="paypal-button-container"]');
        for (const container of button_containers) {
            // Check if button has already been rendered.
            if (!container.innerHTML) {
                paypal.Buttons({
                    style: {
                        label: 'pay'
                    },
                    createOrder: this.create_order.bind(this),
                    onError: () => {},
                    onApprove: this.approve_order.bind(this)
                }).render(`#${container.id}`).then();
            }
        }

    }

    /**
     * Callback to create the PayPal order when the PayPal modal is initially opened.
     * 
     * @returns {Promise<string>} The PayPal order ID.
     */
    create_order(): Promise<string> {
        const totals = this.totals_getter();
        const sales_id = <HTMLInputElement|HTMLSelectElement>document.querySelector('[name="sales_id"]');

        const data: any = {
            order_totals: totals,
            sales_id: sales_id ? sales_id.value : '',
        }

        const selected_address = document.getElementById('js-selected-delivery-address');
        if (selected_address && !selected_address.classList.contains('hidden')) {
            const city_state_zipcode_line = document.getElementById('js-selected-delivery-address-city-state-zipcode').innerHTML;
            data.shipping = {
                name: document.getElementById('js-selected-delivery-address-name').innerHTML,
                address1: document.getElementById('js-selected-delivery-address-address1').innerHTML,
                address2: document.getElementById('js-selected-delivery-address-address2').innerHTML,
                city: city_state_zipcode_line.substring(0, city_state_zipcode_line.lastIndexOf(',')).trim(),
                state: city_state_zipcode_line.substring(city_state_zipcode_line.lastIndexOf(',')+1, city_state_zipcode_line.lastIndexOf(' ')).trim(),
                zipcode: city_state_zipcode_line.substring(city_state_zipcode_line.lastIndexOf(' ')).trim(),
                country: document.getElementById('js-selected-delivery-address-country').innerHTML
            }    
        }

        return new Promise((resolve, reject) => {
            $.ceAjax('request', 'index.php?dispatch=tv_payments.create_paypal_order', {
                method: 'POST',
                caching: false,
                hidden: true,
                data: data,
                callback: response => {
                    if (response.json && response.json.status === "CREATED") {
                        resolve(response.json.id);
                    } else {
                        reject(new Error('Failed to initialize PayPal order'));
                    }
                }
            });
        })

    }

    /**
     * Updates the inputs and runs the callback when the user approves their order in the PayPal modal.
     * 
     * @param {{[key: string]: any}} data about the order passed by the global paypal object.
     * @returns {void}
     */
    approve_order(data: {[key: string]: any}): void {
        if (this.order_id_input && this.payer_id_input) {
            this.order_id_input.value = data.orderID;
            this.payer_id_input.value = data.payerID;
        }

        this.approve_callback();
    }

}
